import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["value", "placeholder", "disabled", "max", "min"]

import { ref, watch, computed } from 'vue';
import { i18n } from '@/i18n';


export default /*@__PURE__*/_defineComponent({
  __name: 'NumberInputWithComma',
  props: {
  modelValue: {
    type: Number,
    required: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  usePlaceholder: {
    type: Boolean,
    default: false,
  },
  autoResize: {
    type: Boolean,
    default: false,
  },
  suffix: {
    type: String,
    default: '',
  },
},
  emits: ['update:modelValue', 'input'],
  setup(__props, { emit: __emit }) {

const locale = computed(() => i18n.locale);
const toLocaleStringOptions: Intl.NumberFormatOptions = {
  style: 'decimal',
  maximumFractionDigits: 7,
};

const inputRef = ref<HTMLInputElement | null>(null);
const isFocused = ref<boolean>(false);

const props = __props;
const emits = __emit;

/**
 * inputのrefを設定する
 * @param ref - inputのref
 */
function setInputRef(ref: any) {
  inputRef.value = ref;
}

/**
 * フォーカス状態を設定する
 * @param focused - フォーカス状態
 */
const setFocused = (focused: boolean): void => {
  isFocused.value = focused;
  if (!inputRef.value) return;
  inputRef.value.type = focused ? 'number' : 'text';
  if (!focused) {
    emits('update:modelValue', parseNumber(inputRef.value.value));
  }
};

/**
 * 数値をフォーマットする
 * @param value - Formatする値
 */
function formatNumber(value: number | undefined): string {
  if (value === undefined) {
    return '';
  }
  if (isNaN(value)) {
    return '';
  }
  if (props.usePlaceholder) {
    return (value?.toLocaleString(locale.value, toLocaleStringOptions) ?? '') + props.suffix;
  }
  // TODO: i18n対応
  // https://developer.mozilla.org/ja/docs/Web/JavaScript/Reference/Global_Objects/Number/toLocaleString
  return (value?.toLocaleString(locale.value, toLocaleStringOptions) ?? '0') + props.suffix;
}

/**
 * パースした数値を返す
 * バリデーションも行っており、値のMax/Minをチェックし、小数点以下6桁に制限する
 * @param value - Parseする値
 */
function parseNumber(value: string): number {
  let num = parseFloat(value.replace(/[^\d.-]/g, ''));
  if (isNaN(num)) return 0;
  if (num > Number.MAX_SAFE_INTEGER) num = Number.MAX_SAFE_INTEGER;
  if (num < Number.MIN_SAFE_INTEGER) num = Number.MIN_SAFE_INTEGER;
  return parseFloat(num.toFixed(6)); // 小数点以下6桁に制限
}

/**
 * inputの内容に基づいて幅を調整する関数
 */
const adjustWidth = () => {
  if (props.modelValue === undefined) return;
  if (inputRef.value && props.autoResize) {
    // 値が10億未満の場合は、はみ出してしまうため、幅を調整する
    if (props.modelValue < 1000000) inputRef.value.style.width = `${formatNumber(props.modelValue).length + 3}ch`;
    else if (props.modelValue < 1000000000) inputRef.value.style.width = `${formatNumber(props.modelValue).length + 2}ch`;
    else if (props.modelValue < 1000000000000) inputRef.value.style.width = `${formatNumber(props.modelValue).length + 1}ch`;
    else inputRef.value.style.width = `${formatNumber(props.modelValue).length}ch`;
  }
};

// modelValueが変更された時にadjustWidthを呼び出す
watch(() => props.modelValue, adjustWidth);

// 初期描画時にadjustWidthを呼び出す
watch(inputRef, (newVal) => {
  if (newVal) {
    adjustWidth();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("input", {
    ref: setInputRef,
    value: isFocused.value ? __props.modelValue : formatNumber(__props.modelValue),
    placeholder: __props.placeholder,
    disabled: __props.disabled,
    max: Number.MAX_SAFE_INTEGER,
    min: Number.MIN_SAFE_INTEGER,
    onFocus: _cache[0] || (_cache[0] = () => setFocused(true)),
    onBlur: _cache[1] || (_cache[1] = ($event: any) => (setFocused(false))),
    onInput: _cache[2] || (_cache[2] = 
      (e) => {
        emits('input', parseNumber(e.target?.value ?? ''));
        adjustWidth();
      }
    )
  }, null, 40, _hoisted_1))
}
}

})